// ./src/config/carStatuses.ts
// Car status configuration for vehicles
// Used by car forms, selectors, and display helpers

// ============================================
// Types
// ============================================

export const CAR_STATUSES = {
  ACTIVE: 100,
  PAUSED: 110,
  IN_RENT: 200,
  IN_MAINTENANCE: 300,
  IN_REPAIR: 310,
  SOLD: 1000,
} as const;

export type CarStatusValue = (typeof CAR_STATUSES)[keyof typeof CAR_STATUSES];

export interface CarStatusOption {
  /** Numeric value stored in database */
  value: string;
  /** Translation key for full label - use with t('common:carStatuses.active') */
  labelKey: string;
  /** Translation key for short label - use with t('common:carStatuses.activeShort') */
  labelShortKey: string;
  /** Icon name for UI components */
  iconName: string;
  /** Color for status indicator */
  color: string;
}

// ============================================
// Car Status Options
// ============================================

export const CAR_STATUS_OPTIONS: CarStatusOption[] = [
  { value: String(CAR_STATUSES.ACTIVE), labelKey: "carStatuses.active", labelShortKey: "carStatuses.activeShort", iconName: "Car", color: "green" },
  { value: String(CAR_STATUSES.PAUSED), labelKey: "carStatuses.paused", labelShortKey: "carStatuses.pausedShort", iconName: "PlayerPause", color: "purple" },
  { value: String(CAR_STATUSES.IN_RENT), labelKey: "carStatuses.inRent", labelShortKey: "carStatuses.inRentShort", iconName: "Key", color: "blue" },
  { value: String(CAR_STATUSES.IN_MAINTENANCE), labelKey: "carStatuses.inMaintenance", labelShortKey: "carStatuses.inMaintenanceShort", iconName: "Tool", color: "orange" },
  { value: String(CAR_STATUSES.IN_REPAIR), labelKey: "carStatuses.inRepair", labelShortKey: "carStatuses.inRepairShort", iconName: "Hammer", color: "red" },
  { value: String(CAR_STATUSES.SOLD), labelKey: "carStatuses.sold", labelShortKey: "carStatuses.soldShort", iconName: "Coins", color: "gray" },
];

// ============================================
// Lookup Map
// ============================================

export const CAR_STATUS_MAP: Record<string, CarStatusOption> = Object.fromEntries(
  CAR_STATUS_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Helper Functions
// ============================================

/**
 * Get car status option by value
 * @param value - Car status value (number or string)
 * @returns Car status option or undefined
 */
export const getCarStatusOption = (
  value: number | string | null | undefined
): CarStatusOption | undefined => {
  if (value == null) return undefined;
  return CAR_STATUS_MAP[String(value)];
};

/**
 * Get icon name for a car status
 * @param value - Car status value
 * @returns Icon name or default
 */
export const getCarStatusIcon = (value: number | string | null | undefined): string => {
  return getCarStatusOption(value)?.iconName ?? "CarFront";
};

/**
 * Get color for a car status
 * @param value - Car status value
 * @returns Color string or default
 */
export const getCarStatusColor = (value: number | string | null | undefined): string => {
  return getCarStatusOption(value)?.color ?? "gray";
};